import { z } from 'zod'

import i18n from 'domain/i18n/i18n'

const t = key => i18n.t(`rob.${key}`)

const requiredStr = z.string().min(1, t('survey.validation.required'))

// --- Survey Logs ---

const surveyLogSchema = z.object({
  initialMeasurementCompleted: requiredStr,
  surveyArrivedBerth: requiredStr,
  surveyCommenced: requiredStr,
  surveyCompleted: requiredStr,
  surveyDocumentCompleted: requiredStr,
  surveyorBoardedVessel: requiredStr,
  surveyorLeftBase: requiredStr,
  surveyorLeftVessel: requiredStr,
  surveyorReturnedBase: requiredStr,
  vesselBerthed: requiredStr,
})

// --- Vessel Info ---

const SURVEYOR_FIELDS = [
  'surveyCompany',
  'nameOfSurveyor',
  'companyAddress',
  'city',
  'email',
]

const vesselInfoSchema = z
  .object({
    city: z.string().optional(),
    companyAddress: z.string().optional(),
    dateOfSurvey: requiredStr,
    email: z.string().optional(),
    filledByCrew: z.boolean(),
    flag: requiredStr,
    imoNumber: z.string().regex(/^\d{7}$/, t('survey.validation.imoDigits')),
    nameOfChiefEngineer: requiredStr,
    nameOfMaster: requiredStr,
    nameOfSurveyor: z.string().optional(),
    owner: requiredStr,
    phone: z.string().optional(),
    placeOfSurvey: requiredStr,
    shipName: requiredStr,
    surveyCompany: z.string().optional(),
    terminalName: requiredStr,
    vesselManagement: requiredStr,
    voyageNo: requiredStr,
  })
  .superRefine((data, ctx) => {
    if (!data.filledByCrew) {
      SURVEYOR_FIELDS.forEach(field => {
        if (!data[field] || data[field].trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('survey.validation.required'),
            path: [field],
          })
        }
      })
    }
  })

// --- General tab ---

// --- Seals tab ---

const sealRowSchema = z.object({
  seal1: requiredStr,
  seal2: requiredStr,
})

const sealRowOptionalSchema = z.object({
  seal1: z.string(),
  seal2: z.string(),
})

const sealGroupSchema = z.object({
  hfo: sealRowSchema,
  mdo: sealRowSchema,
  sludge: sealRowSchema,
  ulsfo: sealRowOptionalSchema,
  vlsfo: sealRowSchema,
})

const sealsBunkeringSchema = z.object({
  portside: sealGroupSchema,
  starboard: sealGroupSchema,
})

const sealsSchema = z.object({
  afterBunkering: sealsBunkeringSchema,
  beforeBunkering: sealsBunkeringSchema,
})

const soundingEquipmentRowSchema = z.object({
  calibrationExpiryDate: z.string(),
  serialNo: requiredStr,
})

const draftRowSchema = z.object({
  after: requiredStr,
  before: requiredStr,
})

const draftRowOptionalSchema = z.object({
  after: z.string(),
  before: z.string(),
})

const draftSchema = z.object({
  aft: draftRowSchema,
  fwd: draftRowSchema,
  mid: draftRowOptionalSchema,
})

const temperatureSchema = z.object({
  engineRoomTemperature: requiredStr,
  outsideTemperature: requiredStr,
  seawaterTemperature: requiredStr,
})

const soundingDraftAndTemperatureSchema = z.object({
  draft: draftSchema,
  soundingEquipment: z.object({
    soundingTape: soundingEquipmentRowSchema,
    thermometer: soundingEquipmentRowSchema,
  }),
  temperature: temperatureSchema,
})

const fuelSludgeBilgeAndCommentsSchema = z.object({
  // TODO: add fuel, sludge, bilge & comments fields
})

export const generalTabSchema = z.object({
  fuelSludgeBilgeAndComments: fuelSludgeBilgeAndCommentsSchema,
  seals: sealsSchema,
  soundingDraftAndTemperature: soundingDraftAndTemperatureSchema,
  surveyLog: surveyLogSchema,
  vesselInfo: vesselInfoSchema,
})
